import { Component } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { GenericCrudEditComponent } from 'src/app/libs/components/generic-crud/generic-crud-edit.component';
import { EMPTY_TID, Overhead, OverheadCost, TId } from 'src/app/models';
import { ICRUDResource } from 'src/app/resources';
import { OverheadCostsAddComponent } from '../overhead-costs-add/overhead-costs-add.component';

@Component({
  selector: 'app-overheads-edit',
  templateUrl: './overheads-edit.component.html',
  styleUrl: './overheads-edit.component.sass',
  standalone: false,
})
export class OverheadsEditComponent extends GenericCrudEditComponent<Overhead> {
  override resource = this.api.resources.overheads;

  readonly EMPTY_TID = EMPTY_TID;

  plantsResource = this.api.resources.plants;
  sectionsResource = this.api.resources.sections;
  workcentersResource = this.api.resources.workcenters;
  formatsResource = this.api.resources.formats;

  overheadCostsResource?: ICRUDResource<OverheadCost>;
  overheadCostsAddComponent = OverheadCostsAddComponent;

  displayEntity = () => this.entity!.description;

  override async preFetch(): Promise<void> {
    this.overheadCostsResource = this.api.resources.overheadCosts(this.entity!.id!);
  }

  initFormGroup(): FormGroup {
    const e = this.entity!;
    const group = this.fb.group({
      id: [e.id],
      plantId: [e.plantId],
      sectionId: [e.sectionId],
      workCenterId: [e.workCenterId],
      description: [e.description],
      formatId: [e.formatId],
      companyId: [e.companyId],
    });

    group.controls['plantId'].valueChanges.subscribe(() => {
      group.patchValue({ sectionId: null, workCenterId: null }, { emitEvent: false });
    });
    group.controls['sectionId'].valueChanges.subscribe(() => {
      group.patchValue({ workCenterId: null }, { emitEvent: false });
    });

    return group;
  }

  protected override async mapFormEntity(): Promise<Partial<Overhead>> {
    const v = this.form!.getRawValue();
    return {
      id: v.id as TId,
      plantId: v.plantId,
      sectionId: v.sectionId || undefined,
      workCenterId: v.workCenterId || undefined,
      description: v.description,
      formatId: v.formatId,
      companyId: v.companyId,
    };
  }

  async onOverheadCostsSaved(): Promise<void> {
    const refreshed = await firstValueFrom(this.api.resources.overheads.get(this.entity!.id!));
    this.entity = refreshed;
    this.entityEvent$.emit({ evtType: 'stateChanged', entity: refreshed });
  }

  override async updateEntityAndEmit(): Promise<void> {
    // Persist changes using the generic editor flow.
    await super.updateEntity();

    // Ensure list/grid receives computed fields & latest relations.
    // Some list fields (e.g. currentCost/currentCostDate) can be derived from subresources.
    const refreshed = await firstValueFrom(this.api.resources.overheads.get(this.entity!.id!));
    this.entity = refreshed;
    this.entityChanged.emit(refreshed);
  }
}
